import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Box,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Component } from "../../../../types/component";
import { Airframe } from "../../../../types/airframe";
import { ComponentMetadata } from "../../../../types/componentMetadataConfig";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { formatOffWingTypeLabel } from "../../../../utils/offWingType";
import {
  componentHasAttachedSubAssemblies,
  isLandingGearComponent,
} from "../../../../utils/landingGearAttachUtils";
import type { DisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { getOwnershipStatusLabel } from "../../../../constants/ownershipStatus";
import { shouldShowComponentOwnershipFields } from "../../../../utils/ownershipVisibility";
import { MINI_PACK_ICON_TOOLTIP } from "../../../../utils/miniPackExportJobUtils";

type Props = {
  component: Component;
  onEdit: (c: Component) => void;
  onDelete: (c: Component) => void;
  onMiniPack?: (c: Component) => void;
  airframes?: Airframe[];
  displayPreferences: DisplayPreferences;
};

const getCustomField = (metadata: Record<string, any> | undefined, ...keys: string[]): any => {
  if (!metadata) return undefined;
  return keys.find(key => metadata[key] !== undefined && metadata[key] !== null) 
    ? metadata[keys.find(key => metadata[key] !== undefined && metadata[key] !== null)!]
    : undefined;
};

const formatDateValue = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") {
    // Extract just the date part
    const match = value.match(/(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : value;
  }
  return String(value);
};

const getFieldLabel = (componentType: string, fieldKey: string): string => {
  const fields = ComponentMetadata[componentType] || [];
  const field = fields.find(f => f.key === fieldKey);
  return field?.label || fieldKey;
};

export function ComponentCard({
  component,
  onEdit,
  onDelete,
  onMiniPack,
  airframes = [],
  displayPreferences,
}: Props) {
  const canUpdateComponent = useHasPermission('assets-assemblies', 'update');
  const canDeleteComponent = useHasPermission('assets-assemblies', 'delete');
  const canAccessMiniPackExport = useHasPermission('assets-mini-pack-export', 'read');

  try {
    // Helper function to get attachment info from airframes
    const getAttachmentInfo = (): { attachedTo: string; location: string } => {
      // First check if component has the info
      if (component.attachedToAirframe && component.attachmentLocation) {
        return {
          attachedTo: component.attachedToAirframe,
          location: component.attachmentLocation,
        };
      }
      for (const airframe of airframes) {
        if (airframe.slots && Array.isArray(airframe.slots)) {
          for (const slot of airframe.slots) {
            if (slot.installedComponent && slot.installedComponent.componentId === component.id) {
              return {
                attachedTo: airframe.tailNumber,
                location: slot.locationCode,
              };
            }
          }
        }
      }

      return { attachedTo: "", location: "" };
    };

    const attachmentInfo = getAttachmentInfo();
    const attachedToAirframe = attachmentInfo.attachedTo;
    const attachmentLocation = attachmentInfo.location;
    const isAttachedToAirframe = Boolean(
      attachedToAirframe?.trim() && attachmentLocation?.trim()
    );
    const showOwnershipOnCard = shouldShowComponentOwnershipFields(
      attachedToAirframe,
      attachmentLocation
    );
    const rawOffWingType =
      getCustomField(component.customMetadata, "offWingType", "OffWingType") ||
      component.customMetadata?.OffWingStatus;
    const hasOffWingType =
      rawOffWingType != null && String(rawOffWingType).trim() !== "";
    const shopVisitReasonLabel = getCustomField(
        component.customMetadata,
        "shopVisitReasonLabel",
        "ShopVisitReasonLabel"
      );
    const hasShopVisitReasonLabel =
        shopVisitReasonLabel != null && String(shopVisitReasonLabel).trim() !== "";
    const isLandingGearWithSubAssembly =
      isLandingGearComponent(component) &&
      componentHasAttachedSubAssemblies(component);
    const showOffWingDetails =
      !isAttachedToAirframe && !isLandingGearWithSubAssembly;
    const showAttachmentSection =
      isAttachedToAirframe ||
      showOffWingDetails ||
      (!isAttachedToAirframe && hasShopVisitReasonLabel);
    
    // Extract Engine fields
    const dateLastShopVisit = getCustomField(
      component.customMetadata,
      "engineDateLastShopVisit",
      "dateOfLastShopVisit",
      "lastShopVisitDate"
    );
    const llpStatus = getCustomField(
      component.customMetadata,
      "engineLlpStatus",
      "llpStatus"
    );
    const tsnLastShopVisit = getCustomField(
      component.customMetadata,
      "engineTsnLastShopVisit",
      "tsnLastShopVisit"
    );
    const csnLastShopVisit = getCustomField(
      component.customMetadata,
      "engineCsnLastShopVisit",
      "csnLastShopVisit"
    );
    const currentThrust = getCustomField(
      component.customMetadata,
      "engineCurrentThrust",
      "currentThrust"
    );

    // Extract APU fields
    const apuPartNumber = getCustomField(component.customMetadata, "apuPartNumber");
    const apuTsn = getCustomField(component.customMetadata, "apuTsn");
    const apuCsn = getCustomField(component.customMetadata, "apuCsn");
    const apuLastHsiOverhaulDate = getCustomField(component.customMetadata, "apuLastHsiOverhaulDate");
    const apuLlpStatus = getCustomField(component.customMetadata, "apuLlpStatus");

    // Extract Landing Gear fields
    const lgTsn = getCustomField(component.customMetadata, "lgTsn");
    const lgCsn = getCustomField(component.customMetadata, "lgCsn");
    const lgLastOverhaulDate = getCustomField(component.customMetadata, "lgLastOverhaulDate");
    const lgBackToBirth = getCustomField(component.customMetadata, "lgBackToBirth");
    const lgAirframeFamily = String(
      getCustomField(component.customMetadata, "lgAirframeFamily") ?? ""
    ).trim();

    // Extract As Of Date (metadata first, then detail object fallback)
    const detailAsOfDate =
      component.type === "Engine"
        ? (component as any)?.engineDetails?.asOfDate ?? (component as any)?.engineDetails?.AsOfDate
        : component.type === "APU"
        ? (component as any)?.apuDetails?.asOfDate ?? (component as any)?.apuDetails?.AsOfDate
        : component.type === "LandingGear"
        ? (component as any)?.landingGearDetails?.asOfDate ?? (component as any)?.landingGearDetails?.AsOfDate
        : undefined;

    const asOfDate =
      getCustomField(
        component.customMetadata,
        "engineAsOfDate",
        "apuAsOfDate",
        "lgAsOfDate",
        "asOfDate"
      ) ?? detailAsOfDate;

    return (
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#222",
          border: "1px solid #333",
          color: "#fff",
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ color: "hsl(19 101% 51% / 1)", mb: 1 }}>
            {component.type === "Engine" && currentThrust ? component.model +  " - " + currentThrust + " - " + component.serialNumber : component.model +  " - " + component.serialNumber}
          </Typography>
          <Typography sx={{ color: "#ccc", mb: 1 }}>
            Serial: {component.serialNumber}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
            <Chip
              size="small"
              label={component.type}
              sx={{
                backgroundColor: "hsl(19 101% 51% / 0.2)",
                color: "#fff",
                "& .MuiChip-label": { color: "#fff" },
              }}
            />
            {isAttachedToAirframe ? (
              <Chip
                size="small"
                label="Attached"
                sx={{
                  backgroundColor: "hsla(142, 45%, 36%, 0.35)",
                  color: "hsl(142 52% 62%)",
                  border: "1px solid hsla(142, 45%, 42%, 0.55)",
                  fontWeight: 600,
                }}
              />
            ) : (
              <Chip
                size="small"
                label="Offwing"
                sx={{
                  backgroundColor: "hsla(205, 70%, 42%, 0.3)",
                  color: "hsl(205 85% 72%)",
                  border: "1px solid hsla(205, 65%, 48%, 0.5)",
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Typography sx={{ color: "#ccc", mb: 1 }}>
            Manufacturer: {component.manufacturer}
          </Typography>

          {showOwnershipOnCard && displayPreferences.showOperator && (
            <Typography sx={{ color: "#ccc", mb: 1 }}>
              Operator: {component.operatorName?.trim() || "N/A"}
            </Typography>
          )}
          {showOwnershipOnCard && displayPreferences.showOwnershipStatus && (
            <Typography sx={{ color: "#ccc", mb: 1 }}>
              Ownership Status:{" "}
              {getOwnershipStatusLabel(
                component.ownershipStatus,
                component.ownershipStatusLabel
              ) || "N/A"}
            </Typography>
          )}


          {/* Engine Details */}
          {component.type === "Engine" && (
            <>
              {dateLastShopVisit && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("Engine", "engineDateLastShopVisit")}: {formatDateValue(dateLastShopVisit)}</Typography>
              )}
              {llpStatus && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("Engine", "engineLlpStatus")}: {llpStatus}</Typography>
              )}
              {tsnLastShopVisit && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("Engine", "engineTsnLastShopVisit")}: {tsnLastShopVisit}</Typography>
              )}
              {csnLastShopVisit && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("Engine", "engineCsnLastShopVisit")}: {csnLastShopVisit}</Typography>
              )}
              {currentThrust && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("Engine", "engineCurrentThrust")}: {currentThrust}</Typography>
              )}
            </>
          )}

          {/* APU Details */}
          {component.type === "APU" && (
            <>
              {apuPartNumber && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("APU", "apuPartNumber")}: {apuPartNumber}</Typography>
              )}
              {apuTsn && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("APU", "apuTsn")}: {apuTsn}</Typography>
              )}
              {apuCsn && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("APU", "apuCsn")}: {apuCsn}</Typography>
              )}
              {apuLastHsiOverhaulDate && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("APU", "apuLastHsiOverhaulDate")}: {formatDateValue(apuLastHsiOverhaulDate)}</Typography>
              )}
              {apuLlpStatus && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("APU", "apuLlpStatus")}: {apuLlpStatus}</Typography>
              )}
            </>
          )}

          {/* Landing Gear Details */}
          {component.type === "LandingGear" && (
            <>
              {lgAirframeFamily && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>
                  Airframe Type: {lgAirframeFamily}
                </Typography>
              )}
              {lgTsn && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("LandingGear", "lgTsn")}: {lgTsn}</Typography>
              )}
              {lgCsn && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("LandingGear", "lgCsn")}: {lgCsn}</Typography>
              )}
              {lgLastOverhaulDate && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("LandingGear", "lgLastOverhaulDate")}: {formatDateValue(lgLastOverhaulDate)}</Typography>
              )}
              {lgBackToBirth && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>{getFieldLabel("LandingGear", "lgBackToBirth")}: {lgBackToBirth}</Typography>
              )}
            </>
          )}

          {asOfDate && (
            <Typography sx={{ color: "#ccc", mb: 1 }}>
              As Of Date: {formatDateValue(asOfDate)}
            </Typography>
          )}

          {showAttachmentSection && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #333" }}>
            {isAttachedToAirframe ? (
              <>
                <Typography sx={{ color: "#ccc", mb: 1 }}>
                  Attached to: {attachedToAirframe}
                </Typography>
                <Typography sx={{ color: "#ccc", mb: 1 }}>
                  Position: {attachmentLocation}
                </Typography>
              </>
            ) : (
              <>
                {showOffWingDetails && (
                  <Typography sx={{ color: "#ccc", mb: 1 }}>
                    OffWing Type: {" "}
                    {hasOffWingType ? formatOffWingTypeLabel(rawOffWingType) : "—"}
                  </Typography>
                )}
                {hasShopVisitReasonLabel && (
                  <Typography sx={{ color: "#ccc", mb: 1 }}>
                    Inshop Reason: {String(shopVisitReasonLabel)}
                  </Typography>
                )}
              </>
            )}
          </Box>
          )}
        </CardContent>

        <CardActions>
          {canUpdateComponent && canAccessMiniPackExport && onMiniPack && (
            <Tooltip title={MINI_PACK_ICON_TOOLTIP}>
              <IconButton
                size="small"
                onClick={() => onMiniPack(component)}
                sx={{ color: "hsl(19 101% 51% / 1)" }}
                aria-label="Configure mini-pack PDF"
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          )}
          {canUpdateComponent && (
            <IconButton
              size="small"
              onClick={() => onEdit(component)}
              sx={{ color: "hsl(19 101% 51% / 1)" }}
            >
              <EditIcon />
            </IconButton>
          )}
          {canDeleteComponent && (
            <IconButton
              size="small"
              onClick={() => onDelete(component)}
              sx={{ color: "#f44336" }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </CardActions>
      </Card>
    );
  } catch (error) {
    return <Typography color="error">Error rendering component</Typography>;
  }
}
